import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import StorageIcon from '@mui/icons-material/Storage';
import SpeedIcon from '@mui/icons-material/Speed';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DevicesIcon from '@mui/icons-material/Devices';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import useResponsiveSize from '../hooks/useResponsiveSize';

const AdminPage = () => {
  const theme = useTheme();
  const { isAdmin, userProfile, token } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [cache, setCache] = useState(null);
  const [blogCache, setBlogCache] = useState(null);
  const [destinationCache, setDestinationCache] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get responsive sizes
  const { fontSize, spacing, borderRadius } = useResponsiveSize();

  // Debugging
  useEffect(() => {
    console.log('Auth state in AdminPage:', { isAdmin, userProfile });
  }, [isAdmin, userProfile]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError('Failed to fetch metrics. ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchCache = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/cache`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCache(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching cache:', err);
      setError('Failed to fetch cache data. ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogCache = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/cache/blogs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogCache(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching blog cache:', err);
      setError('Failed to fetch blog cache data. ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchDestinationCache = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/cache/destinations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDestinationCache(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching destination cache:', err);
      setError('Failed to fetch destination cache data. ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/users/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserStats(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching user statistics:', err);
      setError('Failed to fetch user statistics. ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/feedback/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError('Failed to fetch feedback data. ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const deleteKey = async (key) => {
    try {
      setLoading(true);
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/admin/cache/${key}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteStatus({ success: true, message: `Cache key deleted successfully` });
      // Refresh cache data
      fetchCache();
    } catch (err) {
      console.error('Error deleting cache key:', err);
      setDeleteStatus({ 
        success: false, 
        message: 'Failed to delete cache key. ' + (err.response?.data?.message || err.message) 
      });
    } finally {
      setLoading(false);
    }
  };

  const clearAllCache = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/admin/cache`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteStatus({ 
        success: true, 
        message: `Cleared ${response.data.data.keysDeleted} cache keys successfully` 
      });
      // Refresh both metrics and cache data
      fetchMetrics();
      fetchCache();
    } catch (err) {
      console.error('Error clearing cache:', err);
      setDeleteStatus({ 
        success: false, 
        message: 'Failed to clear cache. ' + (err.response?.data?.message || err.message) 
      });
    } finally {
      setLoading(false);
    }
  };

  const clearBlogCache = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/admin/cache/blogs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteStatus({ 
        success: true, 
        message: `Cleared ${response.data.data.keysDeleted} blog cache keys successfully` 
      });
      // Refresh blog cache data
      fetchBlogCache();
    } catch (err) {
      console.error('Error clearing blog cache:', err);
      setDeleteStatus({ 
        success: false, 
        message: 'Failed to clear blog cache. ' + (err.response?.data?.message || err.message) 
      });
    } finally {
      setLoading(false);
    }
  };

  const clearDestinationCache = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/admin/cache/destinations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteStatus({ 
        success: true, 
        message: `Cleared ${response.data.data.keysDeleted} destination cache keys successfully` 
      });
      // Refresh destination cache data
      fetchDestinationCache();
    } catch (err) {
      console.error('Error clearing destination cache:', err);
      setDeleteStatus({ 
        success: false, 
        message: 'Failed to clear destination cache. ' + (err.response?.data?.message || err.message) 
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && token) {
      Promise.all([
        fetchMetrics(),
        fetchCache(),
        fetchBlogCache(),
        fetchDestinationCache(),
        fetchUserStats(),
        fetchFeedback()
      ]);
    }
  }, [isAdmin, token]);

  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 15, mb: 5 }}>
        <Alert severity="error" sx={{
          '& .MuiAlert-message': {
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }
        }}>
          You do not have permission to access this page. This page is only accessible to administrators.
          <Box mt={2}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>User role: {userProfile?.role || 'unknown'}</Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>isAdmin: {isAdmin ? 'true' : 'false'}</Typography>
          </Box>
        </Alert>
      </Container>
    );
  }

  const renderFeedbackSection = () => {
    return (
      <Paper 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        elevation={2} 
        sx={{ 
          p: 0,
          mb: 4, 
          borderRadius: borderRadius.medium,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          p: isMobile ? 2 : 3,
          gap: isMobile ? 1 : 0,
          backgroundColor: 'background.subtle'
        }}>
          <Typography variant="h5" component="h2" sx={{ 
            display: 'flex', 
            alignItems: 'center',
            fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
          }}>
            <PeopleAltIcon sx={{ mr: 1, fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' } }} /> User Feedback
            {feedback && (
              <Chip 
                label={`${feedback.count} items`} 
                size="small" 
                color="primary" 
                sx={{ ml: 2, fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.75rem' } }} 
              />
            )}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />}
            onClick={fetchFeedback}
            disabled={loading}
            sx={{
              py: isMobile ? 0.5 : 1,
              px: isMobile ? 1.5 : 2,
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
              minWidth: isMobile ? 'auto' : undefined,
            }}
          >
            Refresh
          </Button>
        </Box>
        
        <Divider />
        
        <Box sx={{ p: isMobile ? 2 : 3 }}>
          {loading && !feedback ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress size={isMobile ? 32 : 40} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ 
              mb: 3,
              '& .MuiAlert-message': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }
            }}>{error}</Alert>
          ) : feedback && feedback.rows && feedback.rows.length > 0 ? (
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
              <Table stickyHeader sx={{ minWidth: 650 }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 'bold' }}>Subject</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 'bold' }}>Message</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 'bold' }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feedback.rows.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>{item.name}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>{item.email}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>{item.subject}</TableCell>
                      <TableCell sx={{ 
                        maxWidth: 200, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                      }}>
                        {item.message}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.status} 
                          size="small" 
                          color={
                            item.status === 'new' ? 'info' : 
                            item.status === 'in-progress' ? 'warning' : 
                            'success'
                          }
                          sx={{ 
                            fontWeight: 500,
                            fontSize: { xs: '0.65rem', sm: '0.7rem' }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>{new Date(item.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{
              '& .MuiAlert-message': {
                fontSize: { xs: '0.75rem', sm: '0.8rem' }
              }
            }}>No feedback submissions found.</Alert>
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4, mt: { xs: 10, md: 12 } }}>
        <Box sx={{ 
          mb: 4, 
          display: 'flex', 
          flexDirection: 'row',  // Always row for better alignment
          alignItems: 'center', 
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DeveloperModeIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }, mr: 2, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ 
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
            }}>
              Admin Dashboard
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }} />} 
            onClick={() => {
              fetchMetrics();
              fetchCache();
              fetchBlogCache();
              fetchDestinationCache();
              fetchUserStats();
              fetchFeedback();
            }}
            disabled={loading}
            size={isMobile ? "small" : "medium"}
            sx={{
              py: isMobile ? 0.75 : 1.5,
              px: isMobile ? 1.5 : 3,
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
              borderRadius: borderRadius.medium,
            }}
          >
            Refresh Data
          </Button>
        </Box>

        {deleteStatus && (
          <Alert 
            severity={deleteStatus.success ? "success" : "error"} 
            sx={{ 
              mb: 3,
              '& .MuiAlert-message': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }
            }}
            onClose={() => setDeleteStatus(null)}
          >
            {deleteStatus.message}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
            <CircularProgress size={isMobile ? 36 : 40} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ 
            mb: 4,
            '& .MuiAlert-message': {
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }
          }}>{error}</Alert>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* User Stats Overview */}
            <Typography variant="h5" sx={{ 
              mt: 4, 
              mb: 2, 
              fontWeight: 'bold',
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
            }}>
              <PeopleAltIcon sx={{ 
                verticalAlign: 'middle', 
                mr: 1,
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
              }} />
              User Statistics
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* User Overview Card */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PeopleAltIcon sx={{ 
                        fontSize: { xs: '1.2rem', sm: '1.5rem' }, 
                        mr: 1, 
                        color: 'primary.main' 
                      }} />
                      <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        User Overview
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    {userStats ? (
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Total Users
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {userStats.totalUsers}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Active Users (30d)
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {userStats.activeUsers}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Active Sessions
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {userStats.activeSessions}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Active Rate
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {userStats.totalUsers > 0 
                              ? `${((userStats.activeUsers / userStats.totalUsers) * 100).toFixed(1)}%` 
                              : 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    ) : (
                      <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                        No user statistics available
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Device Distribution Card */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <DevicesIcon sx={{ 
                        fontSize: { xs: '1.2rem', sm: '1.5rem' }, 
                        mr: 1, 
                        color: 'primary.main' 
                      }} />
                      <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        User Devices
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    {userStats && userStats.deviceDistribution ? (
                      <Grid container spacing={2}>
                        {userStats.deviceDistribution.slice(0, 4).map((device, index) => (
                          <Grid item xs={6} key={index}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                              {device.device || 'Unknown'}
                            </Typography>
                            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                              {device.count}
                            </Typography>
                          </Grid>
                        ))}
                        
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Browser Usage
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {userStats.browserDistribution.slice(0, 5).map((browser, index) => (
                              <Chip 
                                key={index}
                                label={`${browser.browser}: ${browser.count}`}
                                size="small"
                                color={index === 0 ? "primary" : "default"}
                                variant={index === 0 ? "filled" : "outlined"}
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
                              />
                            ))}
                          </Box>
                        </Grid>
                      </Grid>
                    ) : (
                      <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                        No device statistics available
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* User Activity Charts */}
            <Paper sx={{ p: isMobile ? 2 : 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ 
                  fontSize: { xs: '1.2rem', sm: '1.5rem' },
                  mr: 1, 
                  color: 'primary.main' 
                }} />
                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  User Activity (14-day trend)
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {userStats && userStats.dailySignups ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                      Daily Signups
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 'bold' }}>
                              Date
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 'bold' }}>
                              New Users
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userStats.dailySignups.slice(-7).map((day, index) => (
                            <TableRow key={index} hover>
                              <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                {new Date(day.date).toLocaleDateString()}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                {day.count}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                      Daily Logins
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 'bold' }}>
                              Date
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 'bold' }}>
                              Login Count
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userStats.dailyLogins.slice(-7).map((day, index) => (
                            <TableRow key={index} hover>
                              <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                {new Date(day.date).toLocaleDateString()}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                {day.count}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              ) : (
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                  No user activity data available
                </Typography>
              )}
            </Paper>
            
            {/* System Overview */}
            <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              <SpeedIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              System Performance
            </Typography>
            
            {/* System Overview Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* OpenAI Stats Card */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <SpeedIcon sx={{ fontSize: 24, mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">OpenAI API Usage</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    {metrics && metrics.openai ? (
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Total Requests</Typography>
                          <Typography variant="h6">{metrics.openai.totalRequests}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Success Rate</Typography>
                          <Typography variant="h6">{metrics.openai.successRate}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Tokens Used</Typography>
                          <Typography variant="h6">{metrics.openai.tokensUsed.toLocaleString()}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Avg Response Time</Typography>
                          <Typography variant="h6">{(metrics.openai.avgResponseTime / 1000).toFixed(2)}s</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">Last Request</Typography>
                          <Typography variant="body2">
                            {metrics.openai.lastRequestTimestamp ? new Date(metrics.openai.lastRequestTimestamp).toLocaleString() : 'N/A'}
                          </Typography>
                        </Grid>
                        
                        {metrics.openai.dailyStats && metrics.openai.dailyStats.length > 0 && (
                          <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>Daily Usage (Last 7 Days)</Typography>
                            <TableContainer sx={{ maxHeight: 150 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="right">Requests</TableCell>
                                    <TableCell align="right">Tokens</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {metrics.openai.dailyStats.slice(-7).map((day, index) => (
                                    <TableRow key={index} hover>
                                      <TableCell>
                                        {new Date(day.date).toLocaleDateString()}
                                      </TableCell>
                                      <TableCell align="right">{day.requests}</TableCell>
                                      <TableCell align="right">{day.tokens}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Grid>
                        )}
                        
                        {metrics.openai.modelUsage && metrics.openai.modelUsage.length > 0 && (
                          <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>Model Usage</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {metrics.openai.modelUsage.map((model, index) => (
                                <Chip 
                                  key={index}
                                  label={`${model.model}: ${model.tokens.toLocaleString()} tokens`}
                                  size="small"
                                  color={index === 0 ? "primary" : "default"}
                                  variant={index === 0 ? "filled" : "outlined"}
                                />
                              ))}
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    ) : (
                      <Typography>No OpenAI metrics available</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Redis Stats Card */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StorageIcon sx={{ fontSize: 24, mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Redis Cache Status</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    {metrics && metrics.redis ? (
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Connection Status</Typography>
                          <Chip 
                            label={metrics.redis.connected ? "Connected" : "Disconnected"} 
                            color={metrics.redis.connected ? "success" : "error"}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Cache Hit Rate</Typography>
                          <Typography variant="h6">
                            {metrics.redis.cacheStats?.hitRate || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Cached Items</Typography>
                          <Typography variant="h6">
                            {(metrics?.redis?.itineraryKeysCount || 0) + 
                             (blogCache?.totalKeys || 0) + 
                             (destinationCache?.totalKeys || 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Cache Hits/Misses</Typography>
                          <Typography variant="h6">
                            {metrics?.redis?.cacheStats?.hits || 0}/{metrics?.redis?.cacheStats?.misses || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>Cache Distribution</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Chip 
                              label={`Itineraries: ${metrics?.redis?.itineraryKeysCount || 0}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Chip 
                              label={`Blogs: ${blogCache?.totalKeys || 0}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                            <Chip 
                              label={`Destinations: ${destinationCache?.totalKeys || 0}`}
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={clearAllCache}
                            sx={{ mt: 1 }}
                          >
                            Clear All Cache
                          </Button>
                        </Grid>
                      </Grid>
                    ) : (
                      <Typography>No Redis metrics available</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Itinerary Cache Management */}
            <Typography variant="h5" sx={{ 
              mt: 4, 
              mb: 2, 
              fontWeight: 'bold',
              fontSize: isMobile ? fontSize.large : undefined  
            }}>
              <StorageIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Itinerary Cache Management
            </Typography>
            
            <Paper sx={{ p: isMobile ? 2 : 3, mb: 4 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                mb: 2,
                gap: isMobile ? 1 : 0
              }}>
                <Typography variant="h6" sx={{ fontSize: isMobile ? fontSize.medium : undefined }}>
                  Itinerary Cache Entries ({cache?.totalKeys || 0})
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={clearAllCache}
                  disabled={loading || (cache?.totalKeys || 0) === 0}
                  sx={{
                    py: isMobile ? 0.5 : 1,
                    px: isMobile ? 1.5 : 2,
                    fontSize: isMobile ? fontSize.small : undefined,
                    borderRadius: borderRadius.medium,
                  }}
                >
                  Clear All Itinerary Cache
                </Button>
              </Box>
              
              {cache && cache.keys && cache.keys.length > 0 ? (
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cache Key</TableCell>
                        <TableCell align="right">TTL</TableCell>
                        <TableCell align="right">Expires In</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cache.keys.map((entry, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.key}
                          </TableCell>
                          <TableCell align="right">{entry.ttl}</TableCell>
                          <TableCell align="right">{entry.expiresIn}</TableCell>
                          <TableCell align="right">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => deleteKey(entry.key)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" sx={{ py: 2 }}>
                  No itinerary cache entries found
                </Typography>
              )}
            </Paper>

            {/* Blog Cache Management */}
            <Typography variant="h5" sx={{ 
              mt: 4, 
              mb: 2, 
              fontWeight: 'bold',
              fontSize: isMobile ? fontSize.large : undefined
            }}>
              <StorageIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Blog Cache Management
            </Typography>
            
            <Paper sx={{ p: isMobile ? 2 : 3, mb: 4 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                mb: 2,
                gap: isMobile ? 1 : 0
              }}>
                <Typography variant="h6" sx={{ fontSize: isMobile ? fontSize.medium : undefined }}>
                  Blog Cache Entries ({blogCache?.totalKeys || 0})
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={clearBlogCache}
                  disabled={loading || (blogCache?.totalKeys || 0) === 0}
                  sx={{
                    py: isMobile ? 0.5 : 1,
                    px: isMobile ? 1.5 : 2,
                    fontSize: isMobile ? fontSize.small : undefined,
                    borderRadius: borderRadius.medium,
                  }}
                >
                  Clear All Blog Cache
                </Button>
              </Box>
              
              {blogCache && blogCache.keys && blogCache.keys.length > 0 ? (
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cache Key</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Content Preview</TableCell>
                        <TableCell align="right">Expires In</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {blogCache.keys.map((entry, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.key}
                          </TableCell>
                          <TableCell>{entry.type}</TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.preview ? entry.preview.title : 'N/A'}
                          </TableCell>
                          <TableCell align="right">{entry.expiresIn}</TableCell>
                          <TableCell align="right">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => deleteKey(entry.key)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" sx={{ py: 2 }}>
                  No blog cache entries found
                </Typography>
              )}
            </Paper>
            
            {/* Destination Cache Management */}
            <Typography variant="h5" sx={{ 
              mt: 4, 
              mb: 2, 
              fontWeight: 'bold',
              fontSize: isMobile ? fontSize.large : undefined
            }}>
              <StorageIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Destination Cache Management
            </Typography>
            
            <Paper sx={{ p: isMobile ? 2 : 3, mb: 4 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                mb: 2,
                gap: isMobile ? 1 : 0
              }}>
                <Typography variant="h6" sx={{ fontSize: isMobile ? fontSize.medium : undefined }}>
                  Destination Cache Entries ({destinationCache?.totalKeys || 0})
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={clearDestinationCache}
                  disabled={loading || (destinationCache?.totalKeys || 0) === 0}
                  sx={{
                    py: isMobile ? 0.5 : 1,
                    px: isMobile ? 1.5 : 2,
                    fontSize: isMobile ? fontSize.small : undefined,
                    borderRadius: borderRadius.medium,
                  }}
                >
                  Clear All Destination Cache
                </Button>
              </Box>
              
              {destinationCache && destinationCache.keys && destinationCache.keys.length > 0 ? (
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cache Key</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Content Preview</TableCell>
                        <TableCell align="right">Expires In</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {destinationCache.keys.map((entry, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.key}
                          </TableCell>
                          <TableCell>{entry.type}</TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.preview ? entry.preview.name : 'N/A'}
                          </TableCell>
                          <TableCell align="right">{entry.expiresIn}</TableCell>
                          <TableCell align="right">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => deleteKey(entry.key)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" sx={{ py: 2 }}>
                  No destination cache entries found
                </Typography>
              )}
            </Paper>

            {/* User Feedback Management */}
            <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              <PeopleAltIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              User Feedback Management
            </Typography>
            
            {renderFeedbackSection()}

            {/* System Info */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>System Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Server Uptime</Typography>
                  <Typography variant="body1">
                    {metrics ? `${Math.floor(metrics.system.uptime / 3600)} hours, ${Math.floor((metrics.system.uptime % 3600) / 60)} minutes` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Current Server Time</Typography>
                  <Typography variant="body1">
                    {metrics ? new Date(metrics.system.timestamp).toLocaleString() : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </motion.div>
        )}
      </Box>
    </Container>
  );
};

export default AdminPage; 